import { useEffect, useState } from "react";
import { Heart, Search, MapPin, Star, Loader2, ArrowRight, Trash2, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import CustomerLayout from "@/components/customer/CustomerLayout";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { photoSrc } from "@/lib/photos";
import { formatRupiah } from "@/components/property/roomMeta";
import { PROPERTY_TYPE_LABELS } from "@/components/property/PropertyStatusBadge";

export default function CustomerFavoritesPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [removing, setRemoving] = useState(null);

  const load = () => {
    setLoading(true);
    setError(false);
    apiClient.get("/favorites")
      .then((r) => setItems(r.data?.data || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const remove = async (propertyId) => {
    setRemoving(propertyId);
    try {
      await apiClient.delete(`/favorites/${propertyId}`);
      setItems((prev) => prev.filter((x) => x.property_id !== propertyId));
      toast.success("Dihapus dari favorit");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setRemoving(null);
    }
  };

  const locationText = (loc) =>
    `${loc?.district_name || ""}${loc?.district_name && loc?.city_name ? ", " : ""}${loc?.city_name || ""}` || "—";

  return (
    <CustomerLayout testid="customer-favorites-page">
      <h1 className="font-heading text-3xl font-bold tracking-tight">Favorit Saya</h1>
      <p className="mt-2 text-sm text-muted-foreground">Hunian yang kamu simpan untuk dilihat kembali.</p>

      {loading ? (
        <div className="grid place-items-center py-20" data-testid="favorites-loading">
          <Loader2 className="animate-spin text-primary" size={28} />
        </div>
      ) : error ? (
        <div className="mt-6 rounded-2xl border border-destructive/30 bg-destructive/10 p-12 text-center" data-testid="favorites-error">
          <p className="font-heading text-lg font-semibold text-destructive">Gagal memuat favorit.</p>
          <p className="mt-2 text-sm text-muted-foreground">Periksa koneksi Anda lalu coba lagi.</p>
          <button onClick={load} data-testid="favorites-retry-button"
            className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent text-accent-foreground font-semibold hover:scale-105 transition-transform">
            <RotateCcw size={15} /> Coba Lagi
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border bg-secondary/30 p-12 text-center" data-testid="customer-favorites-empty">
          <span className="grid place-items-center w-14 h-14 rounded-2xl bg-secondary text-primary mx-auto mb-4">
            <Heart size={26} />
          </span>
          <h3 className="font-heading text-lg font-medium text-foreground">Belum ada hunian favorit.</h3>
          <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
            Tekan ikon hati pada hunian yang kamu sukai untuk menyimpannya di sini.
          </p>
          <button onClick={() => navigate("/search")} data-testid="customer-favorites-explore-button"
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-accent text-accent-foreground hover:scale-105 active:scale-95 transition-transform">
            <Search size={16} /> Jelajahi Hunian
          </button>
        </div>
      ) : (
        <div className="mt-6 grid sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="favorites-grid">
          {items.map((p, i) => (
            <div key={p.property_id} data-testid={`favorite-item-${i}`}
              className="group bg-card border border-border rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-transform flex flex-col">
              <div className="relative aspect-[4/3] bg-secondary/60 overflow-hidden">
                {p.cover_photo_id ? (
                  <img src={photoSrc(p.cover_photo_id)} alt={p.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-xs text-muted-foreground">Foto hunian</div>
                )}
                <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-primary text-primary-foreground">
                  {PROPERTY_TYPE_LABELS[p.property_type] || p.property_type || "Kos"}
                </span>
                <span
                  data-testid={`favorite-availability-${i}`}
                  className={`absolute top-3 right-3 text-[10px] font-bold px-2.5 py-1 rounded-full ${
                    p.property_status === "published" && p.is_available
                      ? "bg-emerald-500 text-white"
                      : "bg-slate-500 text-white"
                  }`}>
                  {p.property_status !== "published"
                    ? "Tidak aktif"
                    : p.is_available ? "Tersedia" : "Penuh"}
                </span>
              </div>

              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-heading text-lg font-semibold truncate" data-testid={`favorite-name-${i}`}>{p.name || "—"}</h3>
                <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                  <MapPin size={14} /> {locationText(p.location)}
                </p>
                <div className="mt-3 flex items-center gap-1.5 text-sm">
                  <Star size={15} className="text-accent fill-accent" />
                  <span className="font-semibold text-foreground">{p.rating ? p.rating.toFixed(1) : "—"}</span>
                  <span className="text-muted-foreground">({p.review_count || 0} ulasan)</span>
                </div>
                <div className="mt-4 pt-4 border-t border-border flex items-end justify-between">
                  <div data-testid={`favorite-price-${i}`}>
                    <span className="font-heading text-xl font-bold text-primary">
                      {p.min_price != null ? formatRupiah(p.min_price) : "Rp —"}
                    </span>
                    <span className="text-xs text-muted-foreground"> /bln</span>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <button onClick={() => navigate(`/property/${p.property_id}`)}
                    data-testid={`favorite-open-${i}`}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold bg-accent text-accent-foreground hover:scale-[1.02] active:scale-95 transition-transform">
                    Lihat Detail <ArrowRight size={15} />
                  </button>
                  <button onClick={() => remove(p.property_id)} disabled={removing === p.property_id}
                    data-testid={`favorite-remove-${i}`} title="Hapus dari favorit"
                    className="grid place-items-center w-10 h-10 rounded-full border border-border text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-60">
                    {removing === p.property_id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </CustomerLayout>
  );
}
